import { useCallback, useEffect, useRef, useState } from "react";
import { listGitlabUsers } from "../api/gitdashService";
import { fetchIssuesForUser } from "../api/gitdashService";
import { normalizeUserIssuesResponse } from "../utils/issue";
import { TIME_CONSTANTS } from "../constants/time";

const createEmptyUserState = () => ({
  issues: [],
  error: false,
  isRefreshing: false,
});

export const useUserIssues = () => {
  const [gitlabUsers, setGitlabUsers] = useState([]);
  const [userDataMap, setUserDataMap] = useState({});
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isBackgroundRefreshing, setIsBackgroundRefreshing] = useState(false);
  const [countdown, setCountdown] = useState(TIME_CONSTANTS.COUNTDOWN_RESET);
  const isLoadingRef = useRef(false);
  const userDataRef = useRef(userDataMap);

  useEffect(() => {
    userDataRef.current = userDataMap;
  }, [userDataMap]);

  const fetchAllUserIssues = useCallback(
    async (users, isBackgroundRefresh = false) => {
      if (!users?.length) return;

      if (!isBackgroundRefresh) {
        setIsLoading(true);
      }

      const initialUserData = {};
      users.forEach((user) => {
        const existing = userDataRef.current[user.username];
        if (!existing?.issues?.length) {
          initialUserData[user.username] = {
            ...createEmptyUserState(),
            isRefreshing: true,
          };
        } else {
          setUserDataMap((prev) => ({
            ...prev,
            [user.username]: {
              ...prev[user.username],
              isRefreshing: true,
            },
          }));
        }
      });

      if (Object.keys(initialUserData).length > 0) {
        setUserDataMap((prev) => ({ ...prev, ...initialUserData }));
      }

      const userPromises = users.map(async (user) => {
        try {
          const response = await fetchIssuesForUser(user.username);
          const normalized = normalizeUserIssuesResponse(
            user.username,
            response
          );

          if (normalized.error) {
            const errorData = {
              issues: userDataRef.current[user.username]?.issues || [],
              error: true,
              errorMessage: normalized.error,
              lastUpdated: Date.now(),
              isRefreshing: false,
            };

            setUserDataMap((prev) => ({
              ...prev,
              [user.username]: errorData,
            }));

            return { username: user.username, success: false };
          }

          const nextData = {
            issues: normalized.issues,
            error: false,
            lastUpdated: Date.now(),
            isRefreshing: false,
          };

          setUserDataMap((prev) => ({
            ...prev,
            [user.username]: nextData,
          }));

          return { username: user.username, success: true };
        } catch (error) {
          const errorData = {
            issues: userDataRef.current[user.username]?.issues || [],
            error: true,
            errorMessage: error.message || "Failed to fetch issues",
            lastUpdated: Date.now(),
            isRefreshing: false,
          };

          setUserDataMap((prev) => ({
            ...prev,
            [user.username]: errorData,
          }));

          return { username: user.username, success: false };
        }
      });

      await Promise.allSettled(userPromises);

      if (!isBackgroundRefresh) {
        setIsLoading(false);
      }
    },
    []
  );

  const loadGitlabUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      const users = await listGitlabUsers();
      setGitlabUsers(users);
      await fetchAllUserIssues(users);
    } catch (error) {
      console.error("Error loading GitLab users:", error);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [fetchAllUserIssues]);

  const refreshAll = useCallback(async () => {
    if (isLoadingRef.current || !gitlabUsers.length) return;

    isLoadingRef.current = true;
    setCountdown(TIME_CONSTANTS.COUNTDOWN_RESET);
    setIsLoading(true);

    try {
      await fetchAllUserIssues(gitlabUsers, false);
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  }, [fetchAllUserIssues, gitlabUsers]);

  const refreshUser = useCallback(
    async (username) => {
      await fetchAllUserIssues([{ username }], true);
    },
    [fetchAllUserIssues]
  );

  const refreshInBackground = useCallback(async () => {
    if (!gitlabUsers.length || isLoadingRef.current) return;

    setIsBackgroundRefreshing(true);
    try {
      await fetchAllUserIssues(gitlabUsers, true);
    } finally {
      setIsBackgroundRefreshing(false);
    }
  }, [fetchAllUserIssues, gitlabUsers]);

  useEffect(() => {
    loadGitlabUsers();
  }, [loadGitlabUsers]);

  useEffect(() => {
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1 && !isLoadingRef.current) {
          refreshInBackground();
          return TIME_CONSTANTS.COUNTDOWN_RESET;
        }
        if (!isLoadingRef.current) {
          return prev - 1;
        }
        return prev;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [refreshInBackground]);

  return {
    gitlabUsers,
    userDataMap,
    isLoadingUsers,
    isLoading,
    isBackgroundRefreshing,
    countdown,
    refreshAll,
    refreshUser,
  };
};
